import React, { useState, useEffect, useCallback } from 'react';
import './EligibilityPage.css';
import EligibilityResults from '../components/EligibilityResults';
import { DATE_INPUT_MIN, DATE_INPUT_MAX } from '../utils/dateInputBounds';

const API_URL = process.env.REACT_APP_API_URL || '';

// Service type codes the user can break out into their own results box.
const SERVICE_TYPE_OPTIONS = [
  { code: '30', label: 'Health Benefit Plan Coverage (general)' },
  { code: '42', label: 'Home Health Care' },
  { code: '1', label: 'Medical Care' },
  { code: '33', label: 'Chiropractic' },
  { code: '35', label: 'Dental Care' },
  { code: '47', label: 'Hospital' },
  { code: '48', label: 'Hospital - Inpatient' },
  { code: '50', label: 'Hospital - Outpatient' },
  { code: '51', label: 'Hospital - Emergency Accident' },
  { code: '52', label: 'Hospital - Emergency Medical' },
  { code: '86', label: 'Emergency Services' },
  { code: '88', label: 'Pharmacy' },
  { code: '98', label: 'Professional (Physician) Visit - Office' },
  { code: 'AL', label: 'Vision (Optometry)' },
  { code: 'BZ', label: 'Physician Visit - Office: Well' },
  { code: 'MH', label: 'Mental Health' },
  { code: 'UC', label: 'Urgent Care' },
  { code: 'A3', label: 'Physician Visit - Home' },
  { code: 'A8', label: 'Psychiatric' },
];

function EligibilityPage({ selectedPractice }) {
  const [patients, setPatients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);

  const [formFields, setFormFields] = useState({
    firstName: '', lastName: '', dob: '', memberId: '', payerId: '', payerName: '',
  });

  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [dateOfService, setDateOfService] = useState(new Date().toISOString().slice(0, 10));
  // Service type code to request (one per check) — "30" by default.
  const [serviceType, setServiceType] = useState('30');

  const [results, setResults] = useState(null);
  const [eligHistory, setEligHistory] = useState([]);
  const [activeView, setActiveView] = useState('history');
  const [historyResultData, setHistoryResultData] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Payer search
  const [payerSearchTerm, setPayerSearchTerm] = useState('');
  const [payerSearchResults, setPayerSearchResults] = useState([]);
  const [payerSearching, setPayerSearching] = useState(false);
  const [showPayerSearch, setShowPayerSearch] = useState(false);

  const practicePayers = selectedPractice?.payers || [];

  // Normalize DOB to YYYY-MM-DD for date inputs (handles M/D/YYYY, MM/DD/YYYY, YYYY-MM-DD)
  const normalizeDob = (dob) => {
    if (!dob) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dob)) return dob;
    const parts = dob.split('/');
    if (parts.length === 3) {
      const [m, d, y] = parts;
      return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return dob;
  };

  useEffect(() => {
    if (selectedPractice?.providers?.length > 0) {
      setSelectedProviderId(selectedPractice.providers[0].id);
    } else {
      setSelectedProviderId('');
    }
    setSelectedPatient(null);
    setResults(null);
    setEligHistory([]);
    setActiveView('history');
    setFormFields({ firstName: '', lastName: '', dob: '', memberId: '', payerId: '', payerName: '' });
  }, [selectedPractice]);

  const loadPatients = useCallback(async () => {
    if (!selectedPractice) return;
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/patients`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPatients(data.patients || []);
      }
    } catch (err) { /* supplementary */ }
  }, [selectedPractice]);

  useEffect(() => { loadPatients(); }, [loadPatients]);

  const loadHistory = useCallback(async (patientId) => {
    if (!selectedPractice || !patientId) return;
    try {
      const response = await fetch(`${API_URL}/api/eligibility/history/${selectedPractice.id}/${patientId}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setEligHistory(data.checks || []);
      }
    } catch (err) { /* supplementary */ }
  }, [selectedPractice]);

  const filteredPatients = patients.filter(p => {
    if (!searchTerm) return false;
    const term = searchTerm.toLowerCase();
    return (p.display_name || '').toLowerCase().includes(term) || (p.subscriber_id || '').toLowerCase().includes(term);
  });

  const handleSelectPatient = (patient) => {
    setSelectedPatient(patient);
    setSearchTerm('');
    setFormFields({
      firstName: patient.subscriber_first_name || '',
      lastName: patient.subscriber_last_name || '',
      dob: normalizeDob(patient.subscriber_dob),
      memberId: patient.subscriber_id || '',
      payerId: patient.payer_id || '',
      payerName: patient.payer_name || '',
    });
    setResults(null);
    setError(null);
    setActiveView('history');
    setHistoryResultData(null);
    loadHistory(patient.id);
  };

  const handleClearPatient = () => {
    setSelectedPatient(null);
    setFormFields({ firstName: '', lastName: '', dob: '', memberId: '', payerId: '', payerName: '' });
    setResults(null);
    setError(null);
    setEligHistory([]);
    setActiveView('history');
    setHistoryResultData(null);
  };

  const handleFieldChange = (field, value) => {
    setFormFields(prev => ({ ...prev, [field]: value }));
  };


  const handlePayerChange = (e) => {
    const val = e.target.value;
    if (!val) { setFormFields(prev => ({ ...prev, payerId: '', payerName: '' })); return; }
    const payer = practicePayers.find(p => String(p.id) === val);
    if (payer) setFormFields(prev => ({ ...prev, payerId: payer.payer_id, payerName: payer.payer_name }));
  };

  // Match payer by name+id first (handles duplicate payer_ids like UHC/Optum), fallback to id-only
  // Normalize: lowercase, strip spaces — "United Healthcare" matches "UnitedHealthcare"
  const normName = (n) => (n || '').toLowerCase().replace(/\s+/g, '');
  const matchedPayer = formFields.payerId
    ? practicePayers.find(p => p.payer_id === formFields.payerId && normName(p.payer_name) === normName(formFields.payerName))
      || practicePayers.find(p => p.payer_id === formFields.payerId)
    : null;

  const handlePayerSearch = useCallback(async (term) => {
    setPayerSearchTerm(term);
    if (term.length < 2) { setPayerSearchResults([]); return; }
    setPayerSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/stedi/payers/search?query=${encodeURIComponent(term)}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPayerSearchResults(data.payers || []);
      }
    } catch { /* search is supplementary */ }
    finally { setPayerSearching(false); }
  }, []);

  const handleSelectSearchPayer = (p) => {
    setFormFields(prev => ({ ...prev, payerId: p.payer_id, payerName: p.name }));
    setPayerSearchTerm('');
    setPayerSearchResults([]);
    setShowPayerSearch(false);
  };

  const handleCheck = async () => {
    if (!formFields.payerId || !formFields.memberId) return;
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const response = await fetch(`${API_URL}/api/eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          practiceId: selectedPractice.id,
          patientId: selectedPatient?.id || null,
          providerId: selectedProviderId || null,
          memberId: formFields.memberId,
          firstName: formFields.firstName,
          lastName: formFields.lastName,
          dateOfBirth: formFields.dob,
          payerId: formFields.payerId,
          payerName: formFields.payerName,
          dateOfService: dateOfService,
          serviceTypeCodes: [serviceType],
        }),
      });
      const data = await response.json();
      if (data.success) {
        setResults(data.data);
        setActiveView('results');
        if (selectedPatient) loadHistory(selectedPatient.id);
        loadPatients();
      } else {
        setError(data.error || 'Failed to check eligibility');
        if (data.data) { setResults(data.data); setActiveView('results'); }
      }
    } catch (err) {
      setError('Network error. Please ensure the backend is running.');
    } finally {
      setLoading(false);
    }
  };

  const formatCheckedAt = (isoStr) => {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const formatDos = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const payerInDropdown = !formFields.payerId || !!matchedPayer;
  const todayStr = new Date().toISOString().slice(0, 10);
  const canSubmit = formFields.payerId && formFields.memberId && formFields.firstName && formFields.lastName && formFields.dob && !loading;

  if (!selectedPractice) {
    return (
      <div className="eligibility-page">
        <h1 className="page-title">Eligibility</h1>
        <div className="empty-state"><p>Select a practice to check eligibility.</p></div>
      </div>
    );
  }

  return (
    <div className="eligibility-page">
      <h1 className="page-title">Eligibility</h1>

      <div className="eligibility-content">
        {/* LEFT PANEL */}
        <div className="form-panel">
          <div className="elig-card">
            {/* DOS + Provider */}
            <div className="form-row">
              <div className="form-field">
                <label>Date of Service</label>
                <input type="date" min={DATE_INPUT_MIN} max={DATE_INPUT_MAX} value={dateOfService} onChange={(e) => setDateOfService(e.target.value)} />
              </div>
              <div className="form-field">
                <label>Provider</label>
                <select value={selectedProviderId} onChange={(e) => setSelectedProviderId(e.target.value)}>
                  {(selectedPractice.providers || []).map(prov => (
                    <option key={prov.id} value={prov.id}>
                      {prov.name || `${prov.first_name || ''} ${prov.last_name || ''}`.trim() || prov.npi}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Service Type to request (270) — one per check */}
            <div className="form-row">
              <div className="form-field full">
                <label>Service Type</label>
                <select value={serviceType} onChange={(e) => setServiceType(e.target.value)}>
                  {SERVICE_TYPE_OPTIONS.map(o => (
                    <option key={o.code} value={o.code}>{o.code} — {o.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-divider" />

            {/* Patient */}
            <div className="form-row">
              <div className="form-field full">
                <label>Patient</label>
                <div className="patient-search-wrapper">
                  {selectedPatient ? (
                    <div className="selected-patient-tag">
                      <span>{selectedPatient.display_name}</span>
                      <button className="sp-clear" onClick={handleClearPatient}>&times;</button>
                    </div>
                  ) : (
                    <>
                      <input
                        type="text"
                        placeholder="Search by name or member ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      {searchTerm && filteredPatients.length > 0 && (
                        <div className="patient-dropdown">
                          {filteredPatients.slice(0, 10).map((p) => (
                            <div key={p.id} className="patient-option" onClick={() => handleSelectPatient(p)}>
                              <div className="patient-option-name">{p.display_name}</div>
                              <div className="patient-option-detail">
                                {p.payer_name || 'No payer'} &middot; {p.subscriber_id || 'No member ID'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {searchTerm && filteredPatients.length === 0 && (
                        <div className="patient-dropdown">
                          <div className="patient-option-empty">No patients found</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="form-divider" />

            {/* Subscriber Info */}
            <div className="form-section-label">Subscriber Info</div>
            <div className="form-row">
              <div className="form-field">
                <label>First Name</label>
                <input type="text" value={formFields.firstName} onChange={(e) => handleFieldChange('firstName', e.target.value)} placeholder="John" />
              </div>
              <div className="form-field">
                <label>Last Name</label>
                <input type="text" value={formFields.lastName} onChange={(e) => handleFieldChange('lastName', e.target.value)} placeholder="Doe" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-field">
                <label>Date of Birth</label>
                <input type="date" min="1900-01-01" max={todayStr} value={formFields.dob} onChange={(e) => handleFieldChange('dob', e.target.value)} />
              </div>
              <div className="form-field">
                <label>Member ID</label>
                <input type="text" value={formFields.memberId} onChange={(e) => handleFieldChange('memberId', e.target.value)} placeholder="ABC123456" />
              </div>
            </div>

            {/* Payer */}
            <div className="form-row">
              <div className="form-field full">
                <label>Payer</label>
                {formFields.payerId ? (
                  <div className="payer-manual-display">
                    <span>{formFields.payerName || formFields.payerId}</span>
                    <span className="payer-manual-id">{formFields.payerId}</span>
                    <button className="payer-manual-clear" onClick={() => { setFormFields(prev => ({ ...prev, payerId: '', payerName: '' })); setShowPayerSearch(false); }}>&times;</button>
                  </div>
                ) : showPayerSearch ? (
                  <div className="patient-search-wrapper">
                    <input
                      type="text"
                      placeholder="Search by payer name or ID..."
                      value={payerSearchTerm}
                      onChange={(e) => handlePayerSearch(e.target.value)}
                      autoFocus
                    />
                    {payerSearchTerm.length >= 2 && (
                      <div className="patient-dropdown">
                        {payerSearching && <div className="patient-option-empty">Searching...</div>}
                        {!payerSearching && payerSearchResults.length === 0 && payerSearchTerm.length >= 2 && (
                          <div className="patient-option-empty">No payers found</div>
                        )}
                        {payerSearchResults.map((p, i) => (
                          <div key={i} className="patient-option" onClick={() => handleSelectSearchPayer(p)}>
                            <div className="patient-option-name">{p.name}</div>
                            <div className="patient-option-detail">{p.payer_id}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    <button className="payer-search-back" onClick={() => setShowPayerSearch(false)}>Back to list</button>
                  </div>
                ) : (
                  <>
                    <select value={matchedPayer ? String(matchedPayer.id) : ''} onChange={handlePayerChange}>
                      <option value="">Select a payer...</option>
                      {practicePayers.map(p => (
                        <option key={p.id} value={String(p.id)}>{p.payer_name}</option>
                      ))}
                    </select>
                    <button className="payer-search-link" onClick={() => setShowPayerSearch(true)}>Search all payers</button>
                  </>
                )}
              </div>
            </div>

            <button className="submit-button" onClick={handleCheck} disabled={!canSubmit}>
              {loading ? (
                <><span className="button-spinner"></span>Checking...</>
              ) : (
                <>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                  </svg>
                  Check Eligibility
                </>
              )}
            </button>
          </div>
        </div>

        {/* RIGHT PANEL */}
        <div className="results-panel">
          {error && (
            <div className="error-banner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" />
              </svg>
              <span>{error}</span>
              <button className="error-dismiss" onClick={() => setError(null)}>&times;</button>
            </div>
          )}

          {loading && (
            <div className="loading-state"><div className="spinner"></div><span>Checking eligibility...</span></div>
          )}

          {activeView === 'results' && (results || historyResultData) && !loading && (
            <div className="results-view">
              <EligibilityResults
                data={results || historyResultData}
                onShowHistory={selectedPatient && eligHistory.length > 0 ? () => { setActiveView('history'); setHistoryResultData(null); } : null}
                formContext={{
                  payerId: formFields.payerId,
                  payerName: formFields.payerName,
                  dateOfService: dateOfService,
                  serviceType: serviceType,
                  provider: (selectedPractice.providers || []).find(p => String(p.id) === String(selectedProviderId)),
                }}
              />
            </div>
          )}

          {activeView === 'history' && !loading && (
            <>
              {selectedPatient && eligHistory.length > 0 && (
                <div className="history-section">
                  <h3>Prior Eligibility Checks</h3>
                  <table className="history-table">
                    <thead>
                      <tr>
                        <th className="th-status">Result</th>
                        <th>Name</th>
                        <th>Payer</th>
                        <th>Plan</th>
                        <th>DOS</th>
                        <th>Checked</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eligHistory.map(check => (
                        <tr
                          key={check.id}
                          className="history-row"
                          onClick={() => {
                            if (check.raw_response) {
                              try {
                                const parsed = JSON.parse(check.raw_response);
                                parsed._checkedAt = check.checked_at;
                                setHistoryResultData(parsed);
                                setResults(null);
                                setActiveView('results');
                              } catch { /* malformed response */ }
                            }
                          }}
                        >
                          <td className="td-status">
                            {check.status === 'Active' ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#00a67d"/>
                                <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#d63031"/>
                                <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                              </svg>
                            )}
                          </td>
                          <td>{check.subscriber_first_name} {check.subscriber_last_name}</td>
                          <td>{check.payer_name || check.payer_id}</td>
                          <td>{check.plan_name || '-'}</td>
                          <td>{formatDos(check.service_date)}</td>
                          <td>{formatCheckedAt(check.checked_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedPatient && eligHistory.length === 0 && !results && (
                <div className="empty-state">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                  </svg>
                  <h3>No Prior Checks</h3>
                  <p>No eligibility checks found for {selectedPatient.display_name}.</p>
                </div>
              )}

              {!selectedPatient && !results && (
                <div className="empty-state">
                  <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" />
                  </svg>
                  <h3>Ready to Check Eligibility</h3>
                  <p>Search for a patient or fill in subscriber info manually to verify insurance coverage.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default EligibilityPage;
